import dayjs, { type Dayjs } from 'dayjs';
import { buildToolbarSummaryLabel, resolveDatePreview } from './tableToolbar.helpers';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

export const defaultTableToolbarDateQuickFilter: DateQuickFilter = '7d';

type ColumnOption = {
  field: string;
  mandatory?: boolean;
};

type TableToolbarStateModelParams<TSortKey extends string, TStatus extends string> = {
  columnOptions: ColumnOption[];
  columnVisibilityModel: GridColumnVisibilityModel;
  dateQuickFilter: DateQuickFilter;
  defaultColumnVisibilityModel: GridColumnVisibilityModel;
  defaultSortDirection: 'asc' | 'desc';
  defaultSortKey: TSortKey;
  filterEntities: string[];
  filterItems: string[];
  filterStatuses: TStatus[];
  filterSuites: string[];
  filterTags: string[];
  fromDate: Dayjs | null;
  latestQueryEndDay?: string | null;
  latestQueryStartDay?: string | null;
  previewFromDate?: Dayjs | null;
  previewToDate?: Dayjs | null;
  searchValue: string;
  sortDirection: 'asc' | 'desc';
  sortKey: TSortKey;
  summaryCount?: number | null;
  summaryPluralLabel?: string;
  summarySingularLabel?: string;
  toDate: Dayjs | null;
};

type TableToolbarStateModel = {
  dateRangeLabel: string | null;
  displayedFromDate: Dayjs | null;
  displayedToDate: Dayjs | null;
  hasActiveDateFilters: boolean;
  hasActiveFilters: boolean;
  hasActiveSearch: boolean;
  hasActiveSort: boolean;
  hasActiveToolbarState: boolean;
  hasNonDefaultColumns: boolean;
  mandatoryColumnVisibilityModel: GridColumnVisibilityModel;
};

const resolveMandatoryColumnVisibilityModel = (
  columnOptions: ColumnOption[],
): GridColumnVisibilityModel =>
  columnOptions.reduce<GridColumnVisibilityModel>((acc, option) => {
    if (option.mandatory) {
      acc[option.field] = true;
    }
    return acc;
  }, {});

const resolveHasNonDefaultColumns = ({
  columnOptions,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
}: Pick<
  TableToolbarStateModelParams<string, string>,
  'columnOptions' | 'columnVisibilityModel' | 'defaultColumnVisibilityModel'
>): boolean =>
  columnOptions.some((option) => {
    if (option.mandatory) return false;
    const currentVisible = columnVisibilityModel[option.field];
    const baselineVisible = defaultColumnVisibilityModel[option.field];
    return currentVisible !== baselineVisible;
  });

export const resolveTableToolbarStateModel = <TSortKey extends string, TStatus extends string>({
  columnOptions,
  columnVisibilityModel,
  dateQuickFilter,
  defaultColumnVisibilityModel,
  defaultSortDirection,
  defaultSortKey,
  filterEntities,
  filterItems,
  filterStatuses,
  filterSuites,
  filterTags,
  fromDate,
  latestQueryEndDay = null,
  latestQueryStartDay = null,
  previewFromDate = null,
  previewToDate = null,
  searchValue,
  sortDirection,
  sortKey,
  summaryCount = null,
  summaryPluralLabel,
  summarySingularLabel,
  toDate,
}: TableToolbarStateModelParams<TSortKey, TStatus>): TableToolbarStateModel => {
  const hasNonDefaultColumns = resolveHasNonDefaultColumns({
    columnOptions,
    columnVisibilityModel,
    defaultColumnVisibilityModel,
  });
  const mandatoryColumnVisibilityModel = resolveMandatoryColumnVisibilityModel(columnOptions);
  const hasActiveFilters =
    filterStatuses.length > 0 ||
    filterEntities.length > 0 ||
    filterItems.length > 0 ||
    filterSuites.length > 0 ||
    filterTags.length > 0;
  const hasActiveSearch = searchValue.trim().length > 0;
  const hasActiveDateFilters =
    dateQuickFilter !== defaultTableToolbarDateQuickFilter || fromDate !== null || toDate !== null;
  const hasActiveSort = sortKey !== defaultSortKey || sortDirection !== defaultSortDirection;
  const hasActiveToolbarState =
    hasActiveFilters ||
    hasActiveDateFilters ||
    hasActiveSort ||
    hasNonDefaultColumns ||
    hasActiveSearch;
  const resolvedPreviewFromDate =
    previewFromDate ??
    (dateQuickFilter === 'latest' && fromDate === null && toDate === null && latestQueryStartDay
      ? dayjs(latestQueryStartDay)
      : null);
  const displayedPreviewFromDate = resolvedPreviewFromDate?.isValid()
    ? resolvedPreviewFromDate
    : null;
  const resolvedPreviewToDate =
    previewToDate ??
    (dateQuickFilter === 'latest' && fromDate === null && toDate === null && latestQueryEndDay
      ? dayjs(latestQueryEndDay)
      : null);
  const displayedPreviewToDate = resolvedPreviewToDate?.isValid() ? resolvedPreviewToDate : null;
  const { displayedFromDate, displayedToDate } = resolveDatePreview(
    dateQuickFilter,
    fromDate,
    toDate,
    displayedPreviewFromDate,
    displayedPreviewToDate,
  );
  const dateRangeLabel = buildToolbarSummaryLabel({
    count: summaryCount,
    singularLabel: summarySingularLabel,
    pluralLabel: summaryPluralLabel,
    dateQuickFilter,
    displayedFromDate,
    displayedToDate,
  });

  return {
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    hasActiveDateFilters,
    hasActiveFilters,
    hasActiveSearch,
    hasActiveSort,
    hasActiveToolbarState,
    hasNonDefaultColumns,
    mandatoryColumnVisibilityModel,
  };
};
